import type { BaseSyntheticEvent } from 'react';
import {
  useForm,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useCreateJobPosition } from '../hooks/useCreateJobPosition';
import {
  createJobPositionSchema,
  type CreateJobPositionFormData,
} from '../schemas/jobPosition.schemas';
import { transformFormDataForApi } from './CreateJobPositionDialog.utils';

interface CreateJobPositionForm {
  register: UseFormRegister<CreateJobPositionFormData>;
  errors: FieldErrors<CreateJobPositionFormData>;
  setValue: UseFormSetValue<CreateJobPositionFormData>;
  watch: UseFormWatch<CreateJobPositionFormData>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  isLoading: boolean;
}

export function useCreateJobPositionForm(
  onOpenChange: (open: boolean) => void
): CreateJobPositionForm {
  const { createJobPosition, isLoading } = useCreateJobPosition();
  const form = useForm<CreateJobPositionFormData>({
    resolver: zodResolver(createJobPositionSchema) as never,
    defaultValues: {
      salaryCurrency: 'USD',
      skills: [],
      requiredSkills: [],
      preferredSkills: [],
      technologies: [],
      languages: [],
      certifications: [],
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = form;
  const handleSuccess = (): void => {
    toast.success('Job position created successfully');
    reset();
    onOpenChange(false);
  };
  const onSubmit = handleSubmit((data: CreateJobPositionFormData) => {
    createJobPosition(transformFormDataForApi(data), {
      onSuccess: handleSuccess,
      onError: () => toast.error('Failed to create job position'),
    });
  });

  return { register, errors, setValue, watch, onSubmit, isLoading };
}
